import { OriginBreakdown } from "@/components/common/OriginBreakdown";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Mail, FileSpreadsheet, FileArchive, Smartphone, Building2, ArrowRight } from "lucide-react";
import { KpiNumber } from "@/components/common/KpiNumber";
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { Copy, Trash2, UserPlus, Download, Printer } from "lucide-react";
import { printStandQr } from "@/components/backoffice/EditionPanels";
import { RenewalCard } from "@/components/portal/RenewalCard";
import { Input } from "@/components/ui/input";
import { requestExport } from "@/components/portal/CandidatesTable";
import { api, apiError } from "@/lib/api";
import { downloadBlob } from "@/lib/demo/utils";
import { fullName, fmt } from "@/lib/demo/utils";
import { useI18n } from "@/lib/i18n/i18n";

const Card = ({ children, className = "", testId }) => <div className={cn("rounded-[24px] border border-slate-200 bg-white p-6", className)} data-testid={testId}>{children}</div>;
const Eyebrow = ({ children }) => { const { t } = useI18n(); return <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">{t(children)}</p>; };
const tip = { contentStyle: { borderRadius: 12, border: "1px solid #E2E8F0", fontSize: 12 } };

export const PortalResumen = ({ stats, rows, onOpen }) => {
  const { t } = useI18n();
  return (
  <div className="space-y-6" data-testid="portal-resumen">
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {[["Candidatos", stats.total, "total", true], ["Hoy", stats.today, "today"], ["Día 1", stats.day1, "day1"], ["Día 2", stats.day2, "day2"]].map(([l, v, id, hl]) => (
        <Card key={id} className={hl ? "border-slate-950 bg-slate-950 text-white" : ""}>
          <p className={`text-[11px] font-bold uppercase tracking-[0.2em] ${hl ? "text-white/50" : "text-slate-400"}`}>{t(l)}</p>
          <KpiNumber value={v} testId={`portal-kpi-${id}`} className="mt-3 font-display text-5xl font-bold tracking-tight" />
        </Card>
      ))}
    </div>
    <div className="grid gap-6 xl:grid-cols-3">
      <Card className="xl:col-span-2" testId="portal-hourly-chart">
        <Eyebrow>Candidatos recibidos por hora</Eyebrow>
        <div className="mt-4 h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stats.hourly} barGap={2}>
              <CartesianGrid vertical={false} stroke="#F1F5F9" />
              <XAxis dataKey="hour" tickLine={false} axisLine={false} fontSize={11} stroke="#94A3B8" />
              <YAxis tickLine={false} axisLine={false} fontSize={11} stroke="#94A3B8" width={30} />
              <Tooltip {...tip} cursor={{ fill: "#F8FAFC" }} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="dia1" name={t("Día 1 · 12 mayo")} fill="#CBD5E1" radius={[6, 6, 0, 0]} />
              <Bar dataKey="dia2" name={t(stats.showToday ? "Día 2 · 13 mayo" : "Día 2 · hoy")} fill="#8B0000" radius={[6, 6, 0, 0]} />
              {stats.showToday && <Bar dataKey="hoy" name={t("Hoy")} fill="#0F172A" radius={[6, 6, 0, 0]} />}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
      <Card testId="portal-latest">
        <Eyebrow>Últimos candidatos</Eyebrow>
        <ul className="mt-4 space-y-1">
          {rows.slice(0, 6).map((r) => (
            <li key={r.interaction.id}>
              <button onClick={() => onOpen(r)} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors hover:bg-slate-50" data-testid={`portal-latest-${r.participant.id}`}>
                <span className="font-mono text-xs font-bold text-slate-500">{r.interaction.time}</span>
                <span className="truncate text-sm font-semibold">{fullName(r.participant)}</span>
                {r.interaction.isNew && <span className="ml-auto h-2 w-2 shrink-0 rounded-full bg-upv" />}
              </button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  </div>
  );
};

const HBar = ({ data, color, testId }) => {
  const { t } = useI18n();
  return (
  <div className="mt-4 h-72" data-testid={testId}>
    <ResponsiveContainer width="100%" height="100%">
      <BarChart data={data.map((d) => ({ ...d, name: t(d.name) }))} layout="vertical" margin={{ left: 10 }}>
        <XAxis type="number" hide />
        <YAxis type="category" dataKey="name" width={150} tickLine={false} axisLine={false} fontSize={12} stroke="#475569" />
        <Tooltip {...tip} cursor={{ fill: "#F8FAFC" }} formatter={(v) => fmt(v)} />
        <Bar dataKey="value" name={t("Candidatos")} fill={color} radius={[0, 6, 6, 0]} barSize={18} />
      </BarChart>
    </ResponsiveContainer>
  </div>
  );
};

export const PortalStats = ({ stats }) => (
  <div className="grid gap-6 xl:grid-cols-2" data-testid="portal-stats">
    <Card className="xl:col-span-2" testId="portal-stats-origin"><Eyebrow>Origen de las interacciones</Eyebrow><div className="mt-4"><OriginBreakdown byOrigin={stats.byOrigin} testPrefix="portal-stats" /></div></Card>
    <Card><Eyebrow>Candidatos por titulación</Eyebrow><HBar data={stats.titulaciones} color="#8B0000" testId="portal-stats-titulacion" /></Card>
    <Card><Eyebrow>Candidatos por centro</Eyebrow><HBar data={stats.centros} color="#0F172A" testId="portal-stats-centro" /></Card>
    <Card className="xl:col-span-2">
      <Eyebrow>Lecturas hoy por dispositivo</Eyebrow>
      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        {stats.devices.map((d) => (
          <div key={d.id} className="rounded-2xl bg-slate-50 p-5" data-testid={`portal-stats-device-${d.id}`}>
            <Smartphone className="h-4 w-4 text-upv" /><p className="mt-3 text-sm font-semibold">{d.label}</p>
            <KpiNumber value={d.value} className="mt-1 font-display text-3xl font-bold" />
          </div>
        ))}
      </div>
    </Card>
  </div>
);

export const PortalExport = ({ total }) => {
  const { t } = useI18n();
  return (
  <div className="grid gap-6 lg:grid-cols-2" data-testid="portal-export">
    <Card>
      <FileSpreadsheet className="h-8 w-8 text-emerald-700" />
      <h3 className="mt-5 font-display text-2xl font-bold">{t("Candidatos en Excel")}</h3>
      <p className="mt-2 text-sm text-slate-500">{t("Nombre, contacto, titulación, centro, fecha, hora y dispositivo de lectura. {n} candidatos recibidos.", { n: fmt(total) })}</p>
      <button onClick={() => requestExport("xlsx", {}, t)} className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-emerald-700 font-bold tracking-wide text-white transition-colors hover:bg-emerald-800" data-testid="portal-export-excel-button"><FileSpreadsheet className="h-4 w-4" /> {t("EXPORTAR EXCEL")}</button>
    </Card>
    <Card>
      <FileArchive className="h-8 w-8 text-upv" />
      <h3 className="mt-5 font-display text-2xl font-bold">{t("Todos los CV (ZIP)")}</h3>
      <p className="mt-2 text-sm text-slate-500">{t("Descarga masiva de los currículums recibidos, nombrados por candidato.")}</p>
      <p className="mt-1 text-xs text-slate-400">{t("Se prepara en segundo plano: puedes seguir trabajando. Incluye el Excel de candidatos.")}</p>
      <button onClick={() => requestExport("zip", {}, t)} className="mt-6 flex h-12 w-full items-center justify-center gap-2 rounded-xl border-2 border-slate-900 font-bold tracking-wide transition-colors hover:bg-slate-900 hover:text-white" data-testid="portal-export-zip-button"><FileArchive className="h-4 w-4" /> {t("DESCARGAR CV")}</button>
    </Card>
  </div>
  );
};

const StandQr = ({ company }) => {
  const { t } = useI18n();
  const box = useRef(null);
  const url = `${window.location.origin}/q/${company.qrToken}`;
  const save = () => {
    const c = box.current?.querySelector("canvas");
    if (c) c.toBlob((b) => b && downloadBlob(b, `QR_stand_${company.slug}.png`));
  };
  return (
    <Card testId="portal-stand-qr" className="lg:col-span-2">
      <div className="flex flex-col gap-6 sm:flex-row sm:items-center">
        <div ref={box} className="mx-auto w-48 shrink-0 rounded-2xl border border-slate-200 bg-white p-3 sm:mx-0"><QRCodeCanvas value={url} size={512} level="M" marginSize={1} style={{ width: "100%", height: "auto", display: "block" }} /></div>
        <div className="min-w-0 flex-1">
          <Eyebrow>QR del stand</Eyebrow>
          <p className="mt-2 text-sm text-slate-600">{t("Imprímelo en el stand: los participantes lo escanean y te envían su currículum (opción B).")}</p>
          <p className="mt-3 inline-block rounded-lg bg-slate-50 px-3 py-1.5 font-mono text-xs font-semibold tracking-wider" data-testid="portal-stand-qr-token">{company.qrToken}</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={save} className="flex h-10 items-center gap-2 rounded-xl bg-slate-950 px-4 text-xs font-bold text-white transition-opacity hover:opacity-90" data-testid="portal-stand-qr-download"><Download className="h-3.5 w-3.5" /> {t("Descargar QR")}</button>
            <button onClick={() => printStandQr(box.current, company, t)} className="flex h-10 items-center gap-2 rounded-xl border border-slate-200 px-4 text-xs font-bold transition-colors hover:border-slate-900" data-testid="portal-stand-qr-print"><Printer className="h-3.5 w-3.5" /> {t("Imprimir")}</button>
          </div>
        </div>
      </div>
    </Card>
  );
};

const UsersCard = ({ me, users, onChange }) => {
  const { t } = useI18n();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const isAdmin = me?.companyRole === "admin";
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/company/users", { name, email }); onChange(data.users); setName(""); setEmail(""); toast.success(t("Usuario añadido")); } catch (err) { toast.error(t(apiError(err).message)); } finally { setBusy(false); }
  };
  const remove = async (u) => {
    if (!window.confirm(t("¿Eliminar el acceso de {name}?", { name: u.name }))) return;
    try { const { data } = await api.delete(`/company/users/${u.id}`); onChange(data.users); } catch (err) { toast.error(t(apiError(err).message)); }
  };
  const copy = (u) => { navigator.clipboard?.writeText(`${window.location.origin}/activar/${u.activationToken}`); toast.success(t("Enlace de activación copiado")); };
  const resend = async (u) => { try { const { data } = await api.post(`/company/users/${u.id}/resend-invite`); if (data.inviteStatus === "sent") toast.success(t("Invitación enviada por email")); else toast.warning(t(data.inviteStatus === "skipped" ? "Dominio reservado o de pruebas: email no enviado" : "No se ha podido enviar el email")); } catch (err) { toast.error(t(apiError(err).message)); } };
  return (
    <Card testId="portal-users">
      <Eyebrow>Usuarios y dispositivos (máx. 3)</Eyebrow>
      <ul className="mt-4 space-y-2">
        {users.map((u) => (
          <li key={u.id} className="flex items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3" data-testid={`portal-user-${u.id}`}>
            <span className="min-w-0">
              <span className="flex items-center gap-2 text-sm font-semibold"><span className={`h-2 w-2 shrink-0 rounded-full ${u.pendingActivation ? "bg-amber-400" : "bg-emerald-500"}`} /><span className="truncate">{u.name}</span><span className="shrink-0 rounded bg-white px-1.5 py-0.5 text-[10px] font-bold text-slate-500">{u.deviceId}</span></span>
              <span className="block truncate text-xs text-slate-500">{u.email} · {t(u.companyRole === "admin" ? "Administrador" : "Reclutador")}{u.pendingActivation ? ` · ${t("pendiente de activar")}` : ""}</span>
            </span>
            {isAdmin && (
              <span className="flex shrink-0 gap-1">
                {u.pendingActivation && <button onClick={() => resend(u)} title={t("Reenviar invitación")} className="rounded-lg p-2 text-slate-500 hover:bg-white" data-testid={`portal-user-resend-${u.id}`}><Mail className="h-4 w-4" /></button>}
                {u.pendingActivation && u.activationToken && <button onClick={() => copy(u)} title={t("Copiar enlace de activación")} className="rounded-lg p-2 text-slate-500 hover:bg-white" data-testid={`portal-user-copy-${u.id}`}><Copy className="h-4 w-4" /></button>}
                {u.id !== me.id && <button onClick={() => remove(u)} title={t("Eliminar")} className="rounded-lg p-2 text-slate-500 hover:bg-white hover:text-rose-600" data-testid={`portal-user-remove-${u.id}`}><Trash2 className="h-4 w-4" /></button>}
              </span>
            )}
          </li>
        ))}
      </ul>
      {isAdmin && users.length < 3 && (
        <form onSubmit={add} className="mt-4 grid gap-2 sm:grid-cols-[1fr_1fr_auto]" data-testid="portal-user-add-form">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("Nombre")} className="h-10 rounded-xl" data-testid="portal-user-name-input" />
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className="h-10 rounded-xl" data-testid="portal-user-email-input" />
          <button disabled={busy || !name || !email} className="flex h-10 items-center justify-center gap-1.5 rounded-xl bg-upv px-4 text-xs font-bold text-white disabled:opacity-50" data-testid="portal-user-add-button"><UserPlus className="h-3.5 w-3.5" /> {t("Añadir")}</button>
        </form>
      )}
      {isAdmin && <p className="mt-3 text-xs text-slate-400">{t("Cada nuevo usuario recibe por email un enlace de activación (válido 7 días, un solo uso).")}</p>}
    </Card>
  );
};

export const PortalCuenta = ({ info, onUsers }) => {
  const { t } = useI18n();
  if (!info) return null;
  const c = info.company;
  return (
  <div className="grid gap-6 lg:grid-cols-2" data-testid="portal-cuenta">
    {info.me.companyRole === "admin" && <div className="lg:col-span-2"><RenewalCard /></div>}
    <Card>
      <Building2 className="h-8 w-8 text-upv" />
      <h3 className="mt-5 font-display text-2xl font-bold">{c.name}</h3>
      <dl className="mt-4 space-y-3 text-sm">
        {[["Evento", "Foro de Empleo UPV 2027"], ["Stand", c.stand], ["Sector", c.sector], ["Usuario", info.me.name]].map(([k, v]) => (
          <div key={k} className="flex justify-between gap-4 border-b border-slate-100 pb-3"><dt className="text-slate-500">{t(k)}</dt><dd className="truncate font-semibold">{t(v || "—")}</dd></div>
        ))}
      </dl>
      <div className="mt-4 flex flex-wrap gap-2">
        <a href="/empresa/scanner" className="flex items-center gap-2 rounded-xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white" data-testid="portal-open-scanner-link">{t("Abrir scanner")} <ArrowRight className="h-4 w-4" /></a>
        {c.applicationId && info.me.companyRole === "admin" && <Link to={`/empresas/inscripcion/${c.applicationId}/editar`} className="flex items-center gap-2 rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold" data-testid="portal-edit-profile-link">{t("Editar perfil público")}</Link>}
      </div>
    </Card>
    <UsersCard me={info.me} users={info.users} onChange={onUsers} />
    <StandQr company={c} />
  </div>
  );
};
